import {
  DialogContent,
  Typography,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Divider,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { keyframes } from '@mui/system';
import { useDialogContext } from '../dialog-context';
import { useToastContext } from '../toast-context';
import ResponsiveDialogActions from '../ResponsiveDialogAction';
import { cutDigitFromRight, isNumeric } from '../../utils';
import { services } from '../../pages/Sell/services';
import ConfirmFloorPriceDialog from '../confirm-floor-price-dialog'; // ایمپورت دایلاگ تایید مجزا

export const spin = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const strategies = {
  AUTOMATIC_ESTJT: 'مرجع قیمت',
  MANUAL: 'دستی',
};

const getLocalProfitPercentage = (bullionData, strategy) => {
  let profitPercentage = null;
  let profitPercentageVariable = null;

  if (strategy === 'MANUAL') {
    profitPercentage = bullionData.manualBuybackMarginPercentage;
    profitPercentageVariable = 'manualBuybackMarginPercentage';
  } else if (strategy === 'AUTOMATIC_ESTJT') {
    profitPercentage = bullionData.estjtBuybackMarginPercentage;
    profitPercentageVariable = 'estjtBuybackMarginPercentage';
  }

  return { profitPercentage, profitPercentageVariable };
};

// --- PREMIUM ORANGE UI STYLES (COMPACT) ---
const premiumInputSx = {
  direction: 'ltr',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F1F5F9', // Subtle modern gray
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& fieldset': {
      border: 'none', // Remove default harsh border
    },
    '&:hover': {
      backgroundColor: '#E2E8F0',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow:
        '0 0 0 3px rgba(234, 88, 12, 0.2), 0 4px 12px rgba(0,0,0,0.06)', // Orange 600 glow
    },
    '& .MuiInputBase-input': {
      fontWeight: 700,
      color: '#0F172A',
      fontSize: '0.95rem',
      padding: '10px 14px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748B',
    fontWeight: 600,
    fontSize: '0.85rem',
    '&.Mui-focused': { color: '#ea580c', fontWeight: 700 }, // primary: Orange 600
  },
  '& .MuiFormHelperText-root': {
    color: '#64748B',
    fontWeight: 500,
    mt: 0.5,
    fontSize: '0.75rem',
    textAlign: 'end',
  },
};
// --------------------------

export function BuybackPriceDialog({
  is995,
  isPreview,
  bullionWeight,
  manualBaseBuybackPriceDefault,
  trig,
  trigger,
  onClick,
  profitPercentageDefault,
  cutNumberDefault,
  priceDefault,
  strategyDefault,
  bullionData,
  calculateFloorPriceForBuybackDefault,
}) {
  const [estjPrices, setEstjPrices] = React.useState();
  const [competitorPrice, setCompetitorPrice] = useState(null);
  const [openFloorPriceConfirm, setOpenFloorPriceConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    formState: { isSubmitting, isDirty },
  } = useForm({
    defaultValues: {
      buybackStrategy: strategyDefault,
      calculateFloorPriceForBuyback: !!calculateFloorPriceForBuybackDefault,
    },
  });
  const formValues = watch();
  const strategy = formValues.buybackStrategy;
  const isFloorPriceChecked = !!formValues?.calculateFloorPriceForBuyback; // جهت تغییر پویا استایل بخش کنترل شروط منبع

  const manualBasePrice = !!String(
    formValues?.manualBaseBuybackPrice
  )?.includes(',')
    ? Number(formValues?.manualBaseBuybackPrice?.replace(/,/g, ''))
    : Number(formValues?.manualBaseBuybackPrice) ||
      manualBaseBuybackPriceDefault;

  useEffect(() => {
    services.getEstjPrices().then(prices => {
      setEstjPrices(prices);
    });
  }, [trigger]);

  useEffect(() => {
    services.getCompetitorPrices().then(data => setCompetitorPrice(data));
  }, [trigger]);

  // Polling for estjPrices every 20 seconds
  useEffect(() => {
    const fetchEstjPrices = () => {
      services.getEstjPrices().then(prices => {
        setEstjPrices(prices);
      });
    };

    // Initial fetch (though already handled by trigger effect, this ensures it runs on mount)
    fetchEstjPrices();

    const interval = setInterval(fetchEstjPrices, 20000);

    return () => clearInterval(interval);
  }, []); // Empty dependency array: runs on mount and cleans up on unmount

  const pricePerGram = estjPrices?.gold24k * 10;
  const isCompetitorStrategy = strategy === 'AUTOMATIC_COMPETITOR';
  const isESTJTStrategy = strategy === 'AUTOMATIC_ESTJT';

  const isManualStrategy = strategy === 'MANUAL';
  const marginPercentages = {
    AUTOMATIC_ESTJT: {
      value: 'estjtBuybackMarginPercentage',
      label: 'درصد حاشیه با مرجع قیمت',
    },
    AUTOMATIC_COMPETITOR: {
      value: 'differenceWithCompetitorPercentage',
      label: 'درصد حاشیه با رقیب',
    },
    MANUAL: { value: 'manualBuybackMarginPercentage', label: 'درصد اختلاف' },
  };

  const estjtPricePerGram = !is995 ? pricePerGram : (pricePerGram / 999) * 995;

  const basePrice = isManualStrategy
    ? !is995
      ? manualBasePrice
      : (manualBasePrice / 999) * 995
    : isCompetitorStrategy
      ? competitorPrice?.[bullionWeight]?.tokeniko
      : estjtPricePerGram * bullionWeight;

  const { showToast } = useToastContext();
  const { closeDialog } = useDialogContext();
  const [percentageState, setPercentageState] = useState(
    profitPercentageDefault
  );
  const [appliedCutNumber, setAppliedCutNumber] = useState(cutNumberDefault);
  const [cutDigitBullionPrice, setCutDigitBullionPrice] = useState(0);
  const [activeInput, setActiveInput] = useState(null);
  const [priceWithoutCutNumber, setPriceWithoutCutNumber] = useState(null);
  const [
    previousCalculatedNewBullionPrice,
    setPreviousCalculatedNewBullionPrice,
  ] = useState();

  const manualPriceLessThanThreshhold = false;
  // (!!String(cutDigitBullionPrice)?.includes(',')
  //   ? Number(cutDigitBullionPrice?.replace(/,/g, ''))
  //   : Number(cutDigitBullionPrice)) <=
  // Number(pricePerGram) * bullionWeight * 0.99;
  const manualPriceMoreThanThreshhold = false;
  // (!!String(cutDigitBullionPrice)?.includes(',')
  //   ? Number(cutDigitBullionPrice?.replace(/,/g, ''))
  //   : Number(cutDigitBullionPrice)) >=
  // Number(pricePerGram) * bullionWeight * 1.25;

  const percentageInputName = isPreview
    ? 'manualBuybackMarginPercentage'
    : marginPercentages[strategy]?.value || '';

  const newBullionPrice = !!String(formValues?.price)?.includes(',')
    ? Number(formValues?.price?.replace(/,/g, ''))
    : Number(formValues?.price);

  const newBullionBoardPrice = !!String(formValues?.boardPrice)?.includes(',')
    ? Number(formValues?.boardPrice?.replace(/,/g, ''))
    : Number(formValues?.boardPrice);

  const zeroFreePercentage =
    Math.ceil(
      (newBullionPrice /
        Number(basePrice * (!isManualStrategy ? 1 : bullionWeight)) -
        1) *
        100 *
        1000
    ) / 1000;

  const nominalPercentage =
    Math.ceil(
      (newBullionPrice /
        Number(basePrice * (!isManualStrategy ? 1 : bullionWeight)) -
        1) *
        100 *
        1000
    ) / 1000;

  const calculatedNewBullionPrice =
    basePrice *
    (!isManualStrategy ? 1 : bullionWeight) *
    (1 + zeroFreePercentage / 100);
  useEffect(() => {
    setCutDigitBullionPrice(priceDefault);
    setPriceWithoutCutNumber(priceDefault);
  }, [priceDefault]);

  useEffect(() => {
    if (isManualStrategy) {
      if (percentageState < 0) {
        const boardPrice =
          Number(basePrice) * (!isManualStrategy ? 1 : bullionWeight);

        setValue(
          'boardPrice',
          cutDigitFromRight(
            boardPrice,
            !!formValues?.buybackCutNumber
              ? formValues?.buybackCutNumber
              : cutNumberDefault
          )
        );
      } else if (percentageState >= 0) {
        setValue('boardPrice', Number(cutDigitBullionPrice));
      }
    }
  }, [
    basePrice,
    isManualStrategy,
    bullionWeight,
    percentageState,
    cutNumberDefault,
    formValues?.buybackCutNumber,
  ]);

  useEffect(() => {
    const cutNumber = !!formValues?.buybackCutNumber
      ? formValues?.buybackCutNumber
      : cutNumberDefault;

    setAppliedCutNumber(cutNumber);

    if (activeInput === percentageInputName) {
      setPercentageState(formValues?.[percentageInputName]);

      const percentageSum = Number(formValues?.[percentageInputName]);
      const priceWithPercentage =
        Number(basePrice) *
        (!isManualStrategy ? 1 : bullionWeight) *
        (1 + percentageSum / 100);
      setPriceWithoutCutNumber(priceWithPercentage);

      const finalPrice = cutDigitFromRight(
        priceWithPercentage,
        !!formValues?.buybackCutNumber
          ? formValues?.buybackCutNumber
          : cutNumberDefault
      );

      setValue('price', finalPrice);
      setCutDigitBullionPrice(finalPrice);
    }

    if (
      previousCalculatedNewBullionPrice !== calculatedNewBullionPrice &&
      strategy !== 'MANUAL'
    ) {
      if (activeInput === percentageInputName) {
        setPercentageState(formValues?.[percentageInputName]);
        const percentageSum = Number(formValues?.[percentageInputName]);
        const priceWithPercentage =
          Number(basePrice) *
          (!isManualStrategy ? 1 : bullionWeight) *
          (1 + percentageSum / 100);
        setPriceWithoutCutNumber(priceWithPercentage);

        const finalPrice = cutDigitFromRight(
          priceWithPercentage,
          !!formValues?.buybackCutNumber
            ? formValues?.buybackCutNumber
            : cutNumberDefault
        );
        setValue('price', finalPrice);
        setCutDigitBullionPrice(finalPrice);
      } else if (activeInput === 'buybackCutNumber') {
        setCutDigitBullionPrice(
          cutDigitFromRight(
            Number(priceWithoutCutNumber),
            !!formValues?.buybackCutNumber
              ? formValues?.buybackCutNumber
              : cutNumberDefault
          )
        );
      } else {
        setValue(
          percentageInputName,
          isESTJTStrategy ? nominalPercentage : zeroFreePercentage
        );

        setPercentageState(
          isESTJTStrategy ? nominalPercentage : zeroFreePercentage
        );
        setPriceWithoutCutNumber(calculatedNewBullionPrice);
        setCutDigitBullionPrice(
          cutDigitFromRight(
            calculatedNewBullionPrice,
            !!formValues?.buybackCutNumber
              ? formValues?.buybackCutNumber
              : cutNumberDefault
          )
        );
      }

      setPreviousCalculatedNewBullionPrice(calculatedNewBullionPrice);
    }

    if (activeInput === 'buybackCutNumber') {
      setCutDigitBullionPrice(
        cutDigitFromRight(
          Number(priceWithoutCutNumber),
          !!formValues?.buybackCutNumber
            ? formValues?.buybackCutNumber
            : cutNumberDefault
        )
      );
    }

    if (activeInput === 'manualBaseBuybackPrice') {
      setPriceWithoutCutNumber(calculatedNewBullionPrice);

      const percentageSum = Number(formValues?.[percentageInputName]);
      const priceWithPercentage =
        Number(basePrice) *
        (!isManualStrategy ? 1 : bullionWeight) *
        (1 + percentageSum / 100);

      setCutDigitBullionPrice(
        cutDigitFromRight(
          priceWithPercentage,
          !!formValues?.buybackCutNumber
            ? formValues?.buybackCutNumber
            : buybackCutNumber
        )
      );
    }
    if (activeInput === 'buybackStrategy') {
      const percentageInputName = isPreview
        ? 'manualBuybackMarginPercentage'
        : marginPercentages[strategy]?.value;

      const { profitPercentage } = getLocalProfitPercentage(
        bullionData,
        strategy
      );

      setValue(percentageInputName, profitPercentage);

      setPercentageState(profitPercentage);

      const basePrice = isManualStrategy
        ? !is995
          ? manualBasePrice * bullionWeight
          : ((manualBasePrice * bullionWeight) / 999) * 995
        : isCompetitorStrategy
          ? competitorPrice?.[bullionWeight]?.tokeniko
          : estjtPricePerGram * bullionWeight;

      const priceWithPercentage =
        basePrice * (1 + Number(profitPercentage) / 100);
      setCutDigitBullionPrice(
        cutDigitFromRight(
          priceWithPercentage,
          !!formValues?.cutNumber ? formValues?.cutNumber : cutNumberDefault
        )
      );
    }
  }, [
    pricePerGram,
    calculatedNewBullionPrice,
    newBullionPrice,
    cutNumberDefault,
    profitPercentageDefault,
    formValues?.[percentageInputName],
    formValues?.price,
    formValues?.buybackCutNumber,
    formValues?.manualBaseBuybackPrice,
    activeInput,
  ]);

  useEffect(() => {
    if (activeInput === 'price' || activeInput === 'cutNumber') return;
  }, [activeInput]);

  const onSubmit = async data => {
    try {
      let formData = {};

      for (let key in data) {
        if (key === 'price') {
          data[key] = Number(cutDigitBullionPrice);
        }
        let value = data[key];

        // کنترل نوع داده متغیر بولین پیش از تغییر مقدار
        if (typeof data[key] === 'boolean') {
          value = data[key];
        } else if (!!isNumeric(data[key])) {
          value = !!String(data[key])?.includes(',')
            ? Number(data[key]?.replace(/,/g, ''))
            : Number(data[key]);
        }

        formData = { ...formData, [key]: value };
      }

      formData = {
        ...formData,
        calculateFloorPriceForBuyback: !formData.calculateFloorPriceForBuyback,
      };

      let unitPrice =
        strategy === 'MANUAL'
          ? !is995
            ? manualBasePrice
            : (manualBasePrice / 999) * 995
          : strategy === 'AUTOMATIC_ESTJT'
            ? estjtPricePerGram
            : competitorPrice?.[bullionWeight]?.tokeniko;

      await onClick({
        buybackStrategy: isPreview ? 'MANUAL' : strategy,
        transactionType: 'SELL',
        ...formData,
        unitPrice,
      });

      trig();
      strategy === 'MANUAL' && closeDialog();
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const handleConfirmFloorPrice = () => {
    setValue('calculateFloorPriceForBuyback', true, { shouldDirty: true });
    setOpenFloorPriceConfirm(false);
  };

  const handleCancelFloorPrice = () => {
    setOpenFloorPriceConfirm(false);
  };

  return (
    <DialogContent
      sx={{
        animation: `${spin} 400ms ease-out`,
        dir: 'rtl',
        backgroundColor: 'transparent',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* 1. HERO DASHBOARD (Orange Compact Version) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff0ddff',
          borderRadius: '20px',
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          boxShadow: '0 8px 24px -6px rgba(234, 88, 12, 0.15)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, rgba(234, 90, 12, 0.2), rgba(234, 88, 12, 0))',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            zIndex: 1,
          }}
        >
          <Stack
            flexDirection={'row'}
            alignItems={'center'}
            sx={{ direction: 'ltr' }}
            gap={1}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#c2410c',
                fontWeight: 800,
                fontSize: '0.8rem',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }}
            >
              استراتژی فعلی:
            </Typography>{' '}
            <Box
              sx={{
                bgcolor: '#ea580c',
                color: '#fff',
                px: 1,
                py: 0.25,
                borderRadius: '6px',
                fontSize: '0.7rem',
                fontWeight: 700,
              }}
            >
              {strategies[strategyDefault]}
            </Box>
          </Stack>
          <Typography
            variant="overline"
            sx={{
              color: '#c2410c',
              fontWeight: 800,
              fontSize: '0.8rem',
              letterSpacing: '0.5px',
              lineHeight: 1,
            }}
          >
            قیمت نهایی بازخرید
          </Typography>
        </Box>

        <Typography
          dir="rtl"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
            fontWeight: 800,
            color:
              manualPriceLessThanThreshhold || manualPriceMoreThanThreshhold
                ? '#E11D48'
                : '#9a3412',
            lineHeight: 1.1,
            textShadow: '0 2px 8px rgba(154, 52, 18, 0.08)',
            zIndex: 1,
          }}
        >
          {Number(cutDigitBullionPrice).toLocaleString('fa-IR')}
          <Typography
            component="span"
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              ml: 1,
              color: '#c2410c',
              opacity: 0.8,
            }}
          >
            ریال
          </Typography>
        </Typography>

        <Typography
          dir="rtl"
          sx={{
            color: '#c2410c',
            opacity: 0.85,
            fontSize: '0.8rem',
            fontWeight: 500,
            mt: 1,
            zIndex: 1,
            textAlign: 'center',
          }}
        >
          {`با ${Number(percentageState)}٪ اختلاف از قیمت پایه و  ${Number(appliedCutNumber)} رقم گرد شده `}
        </Typography>

        {isManualStrategy &&
          percentageState < 0 &&
          cutDigitBullionPrice != 0 &&
          Number(formValues?.boardPrice) !== cutDigitBullionPrice && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid rgba(234, 88, 12, 0.2)',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                zIndex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#9a3412', fontWeight: 600, fontSize: '0.85rem' }}
              >
                قیمت تابلو:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#9a3412', fontWeight: 800, fontSize: '0.85rem' }}
              >
                {(!!String(formValues?.boardPrice)?.includes(',')
                  ? Number(formValues?.boardPrice?.replace(/,/g, ''))
                  : Number(formValues?.boardPrice)
                ).toLocaleString('fa-IR')}{' '}
                <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                  ریال
                </Typography>
              </Typography>
            </Box>
          )}
      </Box>

      {/* 2. MAIN FORM INPUTS */}
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: '20px',
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            border: '1px solid #E2E8F0',
          }}
        >
          <Stack gap={2} sx={{ direction: 'ltr' }}>
            {/* Strategy Select */}
            {!isPreview && (
              <>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#334155',
                      mb: 1,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                    }}
                  >
                    تنظیمات پایه بازخرید
                  </Typography>
                  {/* <ُStack flexDirection={'row'} gap={2}> */}
                  <Stack flexDirection={'row'} gap={2}>
                    <Controller
                      defaultValue={strategyDefault}
                      name="buybackStrategy"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          variant="filled"
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiFilledInput-root': {
                              bgcolor: 'transparent',
                              '&:before, &:after': { display: 'none' },
                            },
                          }}
                        >
                          <Select
                            dir="rtl"
                            {...field}
                            onFocus={() => setActiveInput('buybackStrategy')}
                            onBlur={() => setActiveInput(null)}
                            displayEmpty
                            sx={{
                              borderRadius: '12px',
                              backgroundColor: '#F8FAFC',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
                              border: '1px solid #E2E8F0',
                              transition: 'all 0.2s ease',
                              '& .MuiSelect-select': {
                                py: 1.2,
                                px: 1.5,
                                fontWeight: 700,
                                color: '#0F172A',
                                fontSize: '0.95rem',
                              },
                              '&:hover': {
                                borderColor: '#ea580c',
                                boxShadow: '0 2px 8px rgba(234, 88, 12, 0.08)',
                              },
                              '&.Mui-focused': {
                                borderColor: '#ea580c',
                                boxShadow: '0 0 0 3px rgba(234, 88, 12, 0.2)',
                              },
                              '& fieldset': { border: 'none' },
                            }}
                          >
                            <MenuItem
                              value="MANUAL"
                              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                            >
                              قیمت‌گذاری دستی
                            </MenuItem>
                            <MenuItem
                              value="AUTOMATIC_ESTJT"
                              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                            >
                              تنظیم بر اساس مرجع قیمت
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />

                    {!isPreview && strategy === 'MANUAL' && (
                      <Box
                        sx={{
                          flex: 1,

                          p: 1,
                          bgcolor: isFloorPriceChecked ? '#FEF2F2' : '#F1F5F9',
                          borderRadius: '12px',
                          border: isFloorPriceChecked
                            ? '1px dashed #FCA5A5'
                            : '1px dashed #E2E8F0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isFloorPriceChecked ? '#991B1B' : '#334155',
                            fontSize: '0.8rem',
                            transition: 'color 0.2s ease-in-out',
                            direction: 'rtl',
                          }}
                        >
                          نادیده گرفتن قیمت مرجع
                        </Typography>
                        <Controller
                          name="calculateFloorPriceForBuyback"
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={!!value}
                              onChange={e => {
                                const checked = e.target.checked;
                                if (checked) {
                                  setOpenFloorPriceConfirm(true);
                                } else {
                                  onChange(false);
                                }
                              }}
                              color="error"
                              size="small"
                            />
                          )}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
                <Divider sx={{ borderColor: '#E2E8F0' }} />
              </>
            )}

            {/* Calculations Fields */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#334155',
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                پارامترهای محاسبه
              </Typography>

              <Stack gap={2}>
                {strategy === 'MANUAL' && (
                  <Controller
                    name="manualBaseBuybackPrice"
                    control={control}
                    defaultValue={manualBasePrice}
                    disabled={isPreview}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        onFocus={() => setActiveInput('manualBaseBuybackPrice')}
                        onBlur={() => setActiveInput(null)}
                        defaultValue={manualBasePrice}
                        customInput={TextField}
                        disabled={isPreview}
                        thousandSeparator=","
                        placeholder="قیمت هر گرم"
                        fullWidth
                        size="small"
                        label="قیمت هر گرم (ریال)"
                        autoComplete="off"
                        inputProps={{ inputMode: 'numeric' }}
                        decimalScale={0}
                        allowNegative={false}
                        sx={premiumInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <Controller
                    name={
                      isPreview
                        ? 'manualBuybackMarginPercentage'
                        : marginPercentages[strategy]?.value || ''
                    }
                    control={control}
                    defaultValue={profitPercentageDefault}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        onFocus={() =>
                          setActiveInput(
                            isPreview
                              ? 'manualBuybackMarginPercentage'
                              : marginPercentages[strategy]?.value || ''
                          )
                        }
                        onBlur={() => setActiveInput(null)}
                        defaultValue={profitPercentageDefault}
                        customInput={TextField}
                        placeholder={
                          isPreview
                            ? 'درصد اختلاف'
                            : marginPercentages[strategy]?.label || ''
                        }
                        fullWidth
                        size="small"
                        label={
                          isPreview
                            ? 'درصد اختلاف'
                            : marginPercentages[strategy]?.label || ''
                        }
                        autoComplete="off"
                        inputProps={{ inputMode: 'numeric' }}
                        sx={premiumInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />

                  <Controller
                    name="buybackCutNumber"
                    control={control}
                    defaultValue={cutNumberDefault}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        onFocus={() => setActiveInput('buybackCutNumber')}
                        onBlur={() => setActiveInput(null)}
                        defaultValue={cutNumberDefault}
                        customInput={TextField}
                        thousandSeparator=","
                        placeholder="رقم گرد کردن قیمت"
                        fullWidth
                        size="small"
                        label="رقم گرد کردن قیمت"
                        autoComplete="off"
                        inputProps={{ inputMode: 'numeric' }}
                        decimalScale={0}
                        allowNegative={false}
                        sx={premiumInputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Stack>

                {/* سوئیچ وضعیت محاسبه منبع قیمت در صورت برقراری شرط پیش‌نمایش */}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* 3. ACTIONS */}
        <Box sx={{ px: { xs: 0, sm: 1 } }}>
          <ResponsiveDialogActions
            onAccept={handleSubmit(onSubmit)}
            onReject={closeDialog}
            dangerAcceptCall={cutDigitBullionPrice == '0'}
            acceptText={
              manualPriceLessThanThreshhold && cutDigitBullionPrice != '0'
                ? ' کمتر از حدمجاز'
                : manualPriceMoreThanThreshhold
                  ? ' بیشتر از حدمجاز'
                  : isSubmitting
                    ? 'در حال ذخیره...'
                    : cutDigitBullionPrice == '0'
                      ? 'توقف بازخرید'
                      : 'ذخیره تنظیمات'
            }
            acceptDisable={
              !isDirty ||
              isSubmitting ||
              (manualPriceLessThanThreshhold && cutDigitBullionPrice != '0') ||
              manualPriceMoreThanThreshhold
            }
            acceptButtonProps={{
              sx: {
                py: 1.2,
                borderRadius: '12px',
                background: cutDigitBullionPrice == '0' ? '#E11D48' : '#ea580c',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.95rem',
                letterSpacing: '0.5px',
                boxShadow:
                  cutDigitBullionPrice == '0'
                    ? '0 6px 16px rgba(225, 29, 72, 0.2)'
                    : '0 6px 16px rgba(234, 88, 12, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background:
                    cutDigitBullionPrice == '0' ? '#BE123C' : '#c2410c',
                  transform:
                    isDirty && !isSubmitting ? 'translateY(-2px)' : 'none',
                  boxShadow:
                    cutDigitBullionPrice == '0'
                      ? '0 8px 20px rgba(225, 29, 72, 0.3)'
                      : '0 8px 20px rgba(234, 88, 12, 0.3)',
                },
                '&:disabled': {
                  background: '#CBD5E1',
                  color: '#94A3B8',
                  boxShadow: 'none',
                },
              },
            }}
            rejectButtonProps={{
              sx: {
                py: 1.2,
                borderRadius: '12px',
                border: '2px solid transparent',
                color: '#64748B',
                fontWeight: 700,
                fontSize: '0.95rem',
                backgroundColor: '#F1F5F9',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#fff7ed',
                  color: '#ea580c',
                  transform: 'translateY(-2px)',
                },
              },
            }}
          />
        </Box>
      </form>
      <ConfirmFloorPriceDialog
        open={openFloorPriceConfirm}
        onClose={handleCancelFloorPrice}
        onConfirm={handleConfirmFloorPrice}
        title="تایید نادیده گرفتن قیمت مرجع"
        message="این تغییر بدون بررسی مرجع قیمت‌گذاری انجام می‌شود و دارای ریسک مالی است. آیا مطمئن هستید؟"
      />
    </DialogContent>
  );
}
